import { useState, useEffect, useRef, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ComposedChart, Area } from "recharts";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { auth, provider, db } from "./firebase";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import { collection, doc, setDoc, deleteDoc, onSnapshot, query } from "firebase/firestore";

const TYPES = ["Smoked", "Edible", "Oil", "Other"];
const UNITS = { Smoked: "g", Edible: "mg", Oil: "drops", Other: "units" };

const neon = { cyan: "#00f5ff", magenta: "#ff00ff", purple: "#9b59ff", green: "#00ff9f", yellow: "#ffee00" };
const glowStyle = (color, size = 8) => ({ boxShadow: `0 0 ${size}px ${color}, 0 0 ${size * 2}px ${color}40`, borderColor: color });
const textGlow = (color) => ({ textShadow: `0 0 8px ${color}, 0 0 16px ${color}80` });
const inputStyle = { background: "#0a0a1a", border: "1px solid #333", borderRadius: 8, color: "#fff", padding: "10px 14px", width: "100%", boxSizing: "border-box", fontSize: 14, outline: "none" };

// ── Firestore helpers ─────────────────────────────────────────────────────────
const logsCol = (uid) => collection(db, "users", uid, "logs");
const productsCol = (uid) => collection(db, "users", uid, "products");
const settingsDoc = (uid) => doc(db, "users", uid, "meta", "settings");

async function fsSet(col, id, data) { await setDoc(doc(col, String(id)), data); }
async function fsDel(col, id) { await deleteDoc(doc(col, String(id))); }

// ── Particles ─────────────────────────────────────────────────────────────────
function Particles() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let W = canvas.width = window.innerWidth, H = canvas.height = window.innerHeight;
    const particles = Array.from({ length: 80 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 1.5 + 0.5,
      color: [neon.cyan, neon.magenta, neon.purple][Math.floor(Math.random() * 3)],
      alpha: Math.random() * 0.5 + 0.2,
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color + Math.floor(p.alpha * 255).toString(16).padStart(2, "0");
        ctx.shadowBlur = 6; ctx.shadowColor = p.color; ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, zIndex: 0, pointerEvents: "none" }} />;
}

// ── Login Screen ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const handle = async () => {
    setLoading(true); setErr("");
    try { await signInWithPopup(auth, provider); }
    catch (e) { setErr("Sign-in failed. Please try again."); setLoading(false); }
  };
  return (
    <div style={{ minHeight: "100vh", background: "#05050f", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 24, padding: 24, position: "relative" }}>
      <Particles />
      <div style={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: 380, width: "100%" }}>
        <h1 style={{ margin: "0 0 8px", fontSize: 36, fontWeight: 900, letterSpacing: 4, color: neon.cyan, ...textGlow(neon.cyan) }}>◈ MEDLOG</h1>
        <p style={{ color: "#444", fontSize: 13, margin: "0 0 40px", letterSpacing: 2 }}>MEDICINAL CANNABIS TRACKER</p>
        <div style={{ background: "#080818", border: "1px solid #1a1a3a", borderRadius: 20, padding: 36, ...glowStyle(neon.cyan, 4) }}>
          <p style={{ color: "#888", fontSize: 14, margin: "0 0 28px", lineHeight: 1.6 }}>Sign in to sync your data across all your devices.</p>
          <button onClick={handle} disabled={loading} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "1px solid " + neon.cyan, background: loading ? neon.cyan + "11" : "transparent", color: neon.cyan, fontSize: 15, fontWeight: 700, cursor: loading ? "default" : "pointer", ...textGlow(neon.cyan), display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
            {loading ? "Signing in..." : "Continue with Google"}
          </button>
          {err && <p style={{ color: neon.magenta, fontSize: 13, marginTop: 16 }}>{err}</p>}
        </div>
      </div>
    </div>
  );
}

// ── Profile & Settings ────────────────────────────────────────────────────────
function ProfileTab({ user, settings, onSaveSettings, onSignOut }) {
  const [s, setS] = useState(settings);
  const [saved, setSaved] = useState(false);
  const set = (k, v) => setS(f => ({ ...f, [k]: v }));
  const save = () => { onSaveSettings(s); setSaved(true); setTimeout(() => setSaved(false), 1500); };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Profile card */}
      <div style={{ background: "#080818", border: "1px solid #1a1a3a", borderRadius: 16, padding: 24, ...glowStyle(neon.purple, 4), display: "flex", alignItems: "center", gap: 20 }}>
        {user.photoURL
          ? <img src={user.photoURL} alt="avatar" style={{ width: 64, height: 64, borderRadius: "50%", border: "2px solid " + neon.purple, ...glowStyle(neon.purple, 4) }} />
          : <div style={{ width: 64, height: 64, borderRadius: "50%", background: neon.purple + "33", border: "2px solid " + neon.purple, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, color: neon.purple }}>
              {user.displayName?.[0] || "?"}
            </div>
        }
        <div>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>{user.displayName}</div>
          <div style={{ color: "#555", fontSize: 13, marginTop: 2 }}>{user.email}</div>
          <div style={{ color: neon.purple, fontSize: 12, marginTop: 4, ...textGlow(neon.purple) }}>● Synced to cloud</div>
        </div>
      </div>

      {/* Settings */}
      <div style={{ background: "#080818", border: "1px solid #1a1a3a", borderRadius: 16, padding: 24, ...glowStyle(neon.cyan, 4) }}>
        <h2 style={{ margin: "0 0 20px", color: neon.cyan, ...textGlow(neon.cyan), fontSize: 18 }}>⚙️ Settings</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={{ color: "#888", fontSize: 13, display: "block", marginBottom: 6 }}>Display Name</label>
          <input value={s.displayName || ""} onChange={e => set("displayName", e.target.value)} placeholder={user.displayName} style={inputStyle} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ color: "#888", fontSize: 13, display: "block", marginBottom: 8 }}>Default Pain Level</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[1,2,3,4,5,6,7,8,9,10].map(n => {
              const c = n <= 3 ? neon.green : n <= 6 ? neon.yellow : neon.magenta;
              return <button key={n} onClick={() => set("defaultPain", n)} style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${s.defaultPain === n ? c : "#333"}`, background: s.defaultPain === n ? c + "33" : "transparent", color: s.defaultPain === n ? c : "#555", fontWeight: 700, cursor: "pointer", ...(s.defaultPain === n ? textGlow(c) : {}) }}>{n}</button>
            })}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ color: "#888", fontSize: 13, display: "block", marginBottom: 8 }}>Default Consumption Type</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {TYPES.map(t => {
              const colors = { Smoked: neon.cyan, Edible: neon.green, Oil: neon.purple, Other: "#aaa" };
              const c = colors[t];
              return <button key={t} onClick={() => set("defaultType", t)} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${s.defaultType === t ? c : "#333"}`, background: s.defaultType === t ? c + "22" : "transparent", color: s.defaultType === t ? c : "#555", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>{t}</button>
            })}
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ color: "#888", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
            Show streak on Home
            <div onClick={() => set("showStreak", !s.showStreak)} style={{ width: 44, height: 24, borderRadius: 99, background: s.showStreak ? neon.cyan + "44" : "#1a1a2e", border: `1px solid ${s.showStreak ? neon.cyan : "#333"}`, position: "relative", cursor: "pointer", transition: "all 0.2s" }}>
              <div style={{ position: "absolute", top: 3, left: s.showStreak ? 22 : 3, width: 16, height: 16, borderRadius: "50%", background: s.showStreak ? neon.cyan : "#444", transition: "all 0.2s", boxShadow: s.showStreak ? `0 0 6px ${neon.cyan}` : "none" }} />
            </div>
          </label>
        </div>

        <button onClick={save} style={{ width: "100%", padding: "12px", borderRadius: 10, border: "1px solid " + neon.cyan, background: saved ? neon.cyan + "22" : "transparent", color: saved ? neon.green : neon.cyan, fontSize: 15, fontWeight: 700, cursor: "pointer", transition: "all 0.3s", ...textGlow(saved ? neon.green : neon.cyan) }}>
          {saved ? "✓ Saved!" : "Save Settings"}
        </button>
      </div>

      {/* Sign out */}
      <button onClick={onSignOut} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "1px solid #333", background: "transparent", color: "#666", fontSize: 14, cursor: "pointer" }}>
        Sign Out
      </button>
    </div>
  );
}

// ── Reusable UI ───────────────────────────────────────────────────────────────
function PainSlider({ value, onChange }) {
  const color = value <= 3 ? neon.green : value <= 6 ? neon.yellow : neon.magenta;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ color: "#888", fontSize: 13 }}>Pain Level</span>
        <span style={{ color, fontWeight: 700, fontSize: 18, ...textGlow(color) }}>{value}/10</span>
      </div>
      <input type="range" min={1} max={10} value={value} onChange={e => onChange(+e.target.value)} style={{ width: "100%", accentColor: color, cursor: "pointer" }} />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#555" }}><span>Low</span><span>Moderate</span><span>Severe</span></div>
    </div>
  );
}
function PainBadge({ val }) {
  const color = val <= 3 ? neon.green : val <= 6 ? neon.yellow : neon.magenta;
  return <span style={{ color, fontWeight: 700, fontSize: 13, ...textGlow(color) }}>{val}/10</span>;
}
function TypeBadge({ type }) {
  const colors = { Smoked: neon.cyan, Edible: neon.green, Oil: neon.purple, Other: "#aaa" };
  const c = colors[type] || "#aaa";
  return <span style={{ border: `1px solid ${c}`, borderRadius: 6, padding: "2px 8px", fontSize: 12, color: c, ...textGlow(c) }}>{type}</span>;
}
function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "#000000aa", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#0d0d1e", border: "1px solid " + neon.magenta, borderRadius: 16, padding: 28, maxWidth: 320, width: "90%", ...glowStyle(neon.magenta, 6) }}>
        <p style={{ color: "#ccc", fontSize: 15, margin: "0 0 20px", textAlign: "center" }}>{message}</p>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid #333", background: "transparent", color: "#888", cursor: "pointer" }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid " + neon.magenta, background: neon.magenta + "22", color: neon.magenta, fontWeight: 700, cursor: "pointer" }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── Log Form ──────────────────────────────────────────────────────────────────
function LogForm({ onSave, initialValues, onCancel, products, settings }) {
  const now = new Date();
  const pad = n => String(n).padStart(2, "0");
  const localNow = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const defaultType = settings?.defaultType || "Smoked";
  const defaultPain = settings?.defaultPain || 5;
  const [form, setForm] = useState(initialValues || { type: defaultType, amount: "", datetime: localNow, pain: defaultPain, notes: "", productId: "" });
  const [saved, setSaved] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const matchingProducts = products.filter(p => p.type === form.type && p.currentQty > 0);
  useEffect(() => {
    const m = products.filter(p => p.type === form.type && p.currentQty > 0);
    setForm(f => ({ ...f, productId: m.length ? String(m[0].id) : "" }));
  }, [form.type]);
  const submit = () => {
    if (!form.amount) return;
    onSave({ ...form, id: initialValues?.id || Date.now() });
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
    if (!initialValues) setForm(f => ({ ...f, amount: "", notes: "" }));
  };
  return (
    <div style={{ background: "#080818", border: "1px solid #1a1a3a", borderRadius: 16, padding: 24, ...glowStyle(neon.cyan, 4) }}>
      <h2 style={{ margin: "0 0 20px", color: neon.cyan, ...textGlow(neon.cyan), fontSize: 18 }}>{initialValues ? "✏️ Edit Entry" : "+ New Entry"}</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ color: "#888", fontSize: 13, display: "block", marginBottom: 6 }}>Type</label>
          <select value={form.type} onChange={e => set("type", e.target.value)} style={inputStyle}>{TYPES.map(t => <option key={t}>{t}</option>)}</select>
        </div>
        <div>
          <label style={{ color: "#888", fontSize: 13, display: "block", marginBottom: 6 }}>Amount ({UNITS[form.type]})</label>
          <input type="number" min="0" step="0.1" value={form.amount} onChange={e => set("amount", e.target.value)} placeholder="e.g. 0.5" style={inputStyle} />
        </div>
      </div>
      {matchingProducts.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ color: "#888", fontSize: 13, display: "block", marginBottom: 6 }}>Deduct from product <span style={{ color: "#555" }}>(optional)</span></label>
          <select value={form.productId} onChange={e => set("productId", e.target.value)} style={inputStyle}>
            <option value="">— Don't deduct from storage —</option>
            {matchingProducts.map(p => <option key={p.id} value={String(p.id)}>{p.name} ({p.currentQty.toFixed(2)} {UNITS[p.type]} left)</option>)}
          </select>
        </div>
      )}
      <div style={{ marginBottom: 12 }}>
        <label style={{ color: "#888", fontSize: 13, display: "block", marginBottom: 6 }}>Date & Time</label>
        <input type="datetime-local" value={form.datetime} onChange={e => set("datetime", e.target.value)} style={inputStyle} />
      </div>
      <div style={{ marginBottom: 16 }}><PainSlider value={form.pain} onChange={v => set("pain", v)} /></div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ color: "#888", fontSize: 13, display: "block", marginBottom: 6 }}>Notes (optional)</label>
        <input value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="How are you feeling?" style={inputStyle} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {onCancel && <button onClick={onCancel} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1px solid #333", background: "transparent", color: "#666", fontSize: 15, cursor: "pointer" }}>Cancel</button>}
        <button onClick={submit} style={{ flex: 2, padding: "12px", borderRadius: 10, border: "1px solid " + neon.cyan, background: saved ? neon.cyan + "22" : "transparent", color: saved ? neon.green : neon.cyan, fontSize: 15, fontWeight: 700, cursor: "pointer", transition: "all 0.3s", ...textGlow(saved ? neon.green : neon.cyan) }}>
          {saved ? "✓ Saved!" : initialValues ? "Save Changes" : "Log Entry"}
        </button>
      </div>
    </div>
  );
}

// ── Storage Tab ───────────────────────────────────────────────────────────────
function StockBar({ current, max }) {
  const pct = Math.min(100, max > 0 ? (current / max) * 100 : 0);
  const color = pct > 50 ? neon.green : pct > 20 ? neon.yellow : neon.magenta;
  return (
    <div style={{ background: "#0d0d1e", borderRadius: 99, height: 8, overflow: "hidden", marginTop: 6 }}>
      <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 99, boxShadow: `0 0 6px ${color}`, transition: "width 0.4s" }} />
    </div>
  );
}
function StorageTab({ products, onAdd, onRestock, onDelete }) {
  const [showForm, setShowForm] = useState(false);
  const [restockId, setRestockId] = useState(null);
  const [restockAmt, setRestockAmt] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [form, setForm] = useState({ name: "", type: "Smoked", startQty: "", lowThreshold: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submitAdd = () => {
    if (!form.name || !form.startQty) return;
    onAdd({ id: Date.now(), name: form.name, type: form.type, startQty: +form.startQty, currentQty: +form.startQty, lowThreshold: +form.lowThreshold || 0 });
    setForm({ name: "", type: "Smoked", startQty: "", lowThreshold: "" }); setShowForm(false);
  };
  const submitRestock = (id) => {
    if (!restockAmt) return;
    onRestock(id, +restockAmt); setRestockId(null); setRestockAmt("");
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {confirmId && <ConfirmModal message="Remove this product?" onConfirm={() => { onDelete(confirmId); setConfirmId(null); }} onCancel={() => setConfirmId(null)} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, color: neon.cyan, ...textGlow(neon.cyan), fontSize: 18 }}>🗄 Storage</h2>
        <button onClick={() => setShowForm(s => !s)} style={{ padding: "8px 16px", borderRadius: 10, border: "1px solid " + neon.cyan, background: "transparent", color: neon.cyan, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{showForm ? "Cancel" : "+ Add Product"}</button>
      </div>
      {showForm && (
        <div style={{ background: "#080818", border: "1px solid #1a1a3a", borderRadius: 16, padding: 20, ...glowStyle(neon.cyan, 4) }}>
          <h3 style={{ margin: "0 0 16px", color: neon.cyan, fontSize: 15 }}>New Product</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div><label style={{ color: "#888", fontSize: 13, display: "block", marginBottom: 6 }}>Product Name</label><input value={form.name} onChange={e => set("name", e.target.value)} placeholder='e.g. "Blue Dream"' style={inputStyle} /></div>
            <div><label style={{ color: "#888", fontSize: 13, display: "block", marginBottom: 6 }}>Type</label><select value={form.type} onChange={e => set("type", e.target.value)} style={inputStyle}>{TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
            <div><label style={{ color: "#888", fontSize: 13, display: "block", marginBottom: 6 }}>Starting Qty ({UNITS[form.type]})</label><input type="number" min="0" step="0.1" value={form.startQty} onChange={e => set("startQty", e.target.value)} placeholder="e.g. 3.5" style={inputStyle} /></div>
            <div><label style={{ color: "#888", fontSize: 13, display: "block", marginBottom: 6 }}>Low Stock Alert ({UNITS[form.type]})</label><input type="number" min="0" step="0.1" value={form.lowThreshold} onChange={e => set("lowThreshold", e.target.value)} placeholder="e.g. 0.5" style={inputStyle} /></div>
          </div>
          <button onClick={submitAdd} style={{ width: "100%", padding: "11px", borderRadius: 10, border: "1px solid " + neon.green, background: "transparent", color: neon.green, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Add Product</button>
        </div>
      )}
      {products.length === 0 && !showForm && <div style={{ background: "#080818", border: "1px solid #1a1a2e", borderRadius: 16, padding: 32, textAlign: "center" }}><p style={{ color: "#444", margin: 0 }}>No products yet. Add one to start tracking your stock.</p></div>}
      {products.map(p => {
        const pct = p.startQty > 0 ? (p.currentQty / p.startQty) * 100 : 0;
        const isLow = p.lowThreshold > 0 && p.currentQty <= p.lowThreshold;
        const isEmpty = p.currentQty <= 0;
        const statusColor = isEmpty ? neon.magenta : isLow ? neon.yellow : neon.green;
        const statusLabel = isEmpty ? "Empty" : isLow ? "Running Low" : "In Stock";
        return (
          <div key={p.id} style={{ background: "#080818", border: `1px solid ${isEmpty ? neon.magenta + "44" : isLow ? neon.yellow + "44" : "#1a1a3a"}`, borderRadius: 16, padding: 20, ...glowStyle(statusColor, 3) }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{p.name}</span>
                  <TypeBadge type={p.type} />
                  {(isLow || isEmpty) && <span style={{ color: statusColor, fontSize: 11, fontWeight: 700, ...textGlow(statusColor) }}>⚠ {statusLabel}</span>}
                </div>
                <span style={{ color: statusColor, fontSize: 22, fontWeight: 900, ...textGlow(statusColor) }}>{p.currentQty.toFixed(2)}</span>
                <span style={{ color: "#555", fontSize: 13 }}> / {p.startQty} {UNITS[p.type]}</span>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => setRestockId(restockId === p.id ? null : p.id)} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid " + neon.green, background: "transparent", color: neon.green, fontSize: 12, cursor: "pointer" }}>+ Restock</button>
                <button onClick={() => setConfirmId(p.id)} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #333", background: "transparent", color: "#555", fontSize: 14, cursor: "pointer" }}>×</button>
              </div>
            </div>
            <StockBar current={p.currentQty} max={p.startQty} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              <span style={{ color: "#444", fontSize: 11 }}>0 {UNITS[p.type]}</span>
              <span style={{ color: "#444", fontSize: 11 }}>{Math.round(pct)}% remaining</span>
              <span style={{ color: "#444", fontSize: 11 }}>{p.startQty} {UNITS[p.type]}</span>
            </div>
            {restockId === p.id && (
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <input type="number" min="0" step="0.1" value={restockAmt} onChange={e => setRestockAmt(e.target.value)} placeholder={`Add ${UNITS[p.type]}...`} style={{ ...inputStyle, width: "auto", flex: 1 }} />
                <button onClick={() => submitRestock(p.id)} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid " + neon.green, background: neon.green + "22", color: neon.green, fontWeight: 700, cursor: "pointer" }}>Add</button>
                <button onClick={() => { setRestockId(null); setRestockAmt(""); }} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #333", background: "transparent", color: "#666", cursor: "pointer" }}>Cancel</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Daily Summary ─────────────────────────────────────────────────────────────
function DailySummary({ logs, products, settings }) {
  const todayLogs = logs.filter(l => new Date(l.datetime).toDateString() === new Date().toDateString());
  const avgPain = todayLogs.length ? (todayLogs.reduce((s, l) => s + l.pain, 0) / todayLogs.length).toFixed(1) : null;
  const painColor = !avgPain ? "#555" : +avgPain <= 3 ? neon.green : +avgPain <= 6 ? neon.yellow : neon.magenta;
  const streak = (() => {
    let s = 0, d = new Date();
    while (true) {
      const ds = d.toDateString();
      if (logs.some(l => new Date(l.datetime).toDateString() === ds)) { s++; d.setDate(d.getDate() - 1); } else break;
    }
    return s;
  })();
  const lowProducts = products.filter(p => p.lowThreshold > 0 && p.currentQty <= p.lowThreshold);
  const showStreak = settings?.showStreak !== false;
  return (
    <div style={{ background: "#080818", border: "1px solid #1a1a2e", borderRadius: 16, padding: 20, ...glowStyle(neon.cyan, 3) }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h2 style={{ margin: 0, color: neon.cyan, fontSize: 15, ...textGlow(neon.cyan) }}>Today</h2>
        <span style={{ color: "#555", fontSize: 12 }}>{new Date().toLocaleDateString("en-AU", { weekday: "long", month: "long", day: "numeric" })}</span>
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: lowProducts.length || todayLogs.length ? 12 : 0 }}>
        {[{ val: todayLogs.length, label: "Today's Logs", color: neon.cyan }, { val: avgPain || "—", label: "Avg Pain Today", color: painColor }, ...(showStreak ? [{ val: streak, label: "Day Streak 🔥", color: neon.purple }] : [])].map(s => (
          <div key={s.label} style={{ flex: 1, background: "#0d0d1e", borderRadius: 10, padding: "12px", textAlign: "center" }}>
            <div style={{ color: s.color, fontSize: 22, fontWeight: 900, ...textGlow(s.color) }}>{s.val}</div>
            <div style={{ color: "#555", fontSize: 11, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {lowProducts.length > 0 && <div style={{ background: neon.yellow + "11", border: "1px solid " + neon.yellow + "44", borderRadius: 10, padding: "10px 14px", marginBottom: todayLogs.length ? 12 : 0 }}><span style={{ color: neon.yellow, fontSize: 13, ...textGlow(neon.yellow) }}>⚠ Low stock: {lowProducts.map(p => p.name).join(", ")}</span></div>}
      {todayLogs.map(l => (
        <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#0d0d1e", borderRadius: 8, marginBottom: 6 }}>
          <TypeBadge type={l.type} />
          <span style={{ color: "#ccc", fontSize: 12 }}>{l.amount} {UNITS[l.type]}</span>
          <span style={{ color: "#555", fontSize: 11 }}>{new Date(l.datetime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          <span style={{ marginLeft: "auto" }}><PainBadge val={l.pain} /></span>
        </div>
      ))}
    </div>
  );
}

// ── Insights ──────────────────────────────────────────────────────────────────
function Insights({ logs }) {
  const insights = useMemo(() => {
    if (logs.length < 5) return [{ text: "Log at least 5 entries to start seeing insights.", color: "#555" }];
    const results = [];
    const byType = {};
    TYPES.forEach(t => { byType[t] = logs.filter(l => l.type === t); });
    const typePains = TYPES.map(t => ({ type: t, avg: byType[t].length ? byType[t].reduce((s, l) => s + l.pain, 0) / byType[t].length : null })).filter(x => x.avg !== null);
    if (typePains.length >= 2) { const best = typePains.reduce((a, b) => a.avg < b.avg ? a : b); results.push({ text: `${best.type} is associated with your lowest average pain (${best.avg.toFixed(1)}/10).`, color: neon.green }); }
    if (logs.length) results.push({ text: `Your overall average pain is ${(logs.reduce((s, l) => s + l.pain, 0) / logs.length).toFixed(1)}/10.`, color: neon.cyan });
    const recent = [...logs].sort((a, b) => new Date(b.datetime) - new Date(a.datetime)).slice(0, 10);
    const older = [...logs].sort((a, b) => new Date(b.datetime) - new Date(a.datetime)).slice(10, 20);
    if (older.length >= 5) {
      const diff = (recent.reduce((s, l) => s + l.pain, 0) / recent.length) - (older.reduce((s, l) => s + l.pain, 0) / older.length);
      if (Math.abs(diff) > 0.5) results.push({ text: diff < 0 ? "Your pain has been trending down recently 📉" : "Your pain has been trending up recently. Consider discussing with your doctor 📈", color: diff < 0 ? neon.green : neon.magenta });
    }
    const most = TYPES.map(t => ({ t, n: byType[t].length })).reduce((a, b) => a.n > b.n ? a : b);
    if (most.n > 0) results.push({ text: `Most used method: ${most.t} (${most.n} log${most.n > 1 ? "s" : ""})`, color: neon.purple });
    return results;
  }, [logs]);
  return (
    <div style={{ background: "#080818", border: "1px solid #1a1a3a", borderRadius: 16, padding: 24, ...glowStyle(neon.yellow, 4) }}>
      <h2 style={{ margin: "0 0 16px", color: neon.yellow, ...textGlow(neon.yellow), fontSize: 18 }}>💡 Insights</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {insights.map((ins, i) => <div key={i} style={{ padding: "12px 16px", background: "#0d0d1e", borderRadius: 10, borderLeft: `3px solid ${ins.color}`, color: "#ccc", fontSize: 14 }}>{ins.text}</div>)}
      </div>
    </div>
  );
}

// ── Heatmap ───────────────────────────────────────────────────────────────────
function Heatmap({ logs }) {
  const weeks = 18;
  const today = new Date(); today.setHours(0,0,0,0);
  const startDay = new Date(today); startDay.setDate(today.getDate() - (weeks * 7 - 1));
  const days = Array.from({ length: weeks * 7 }, (_, i) => {
    const d = new Date(startDay); d.setDate(startDay.getDate() + i);
    const dl = logs.filter(l => new Date(l.datetime).toDateString() === d.toDateString());
    return { date: d, avg: dl.length ? dl.reduce((s, l) => s + l.pain, 0) / dl.length : null, count: dl.length };
  });
  const cellColor = d => !d.count ? "#0d0d1e" : d.avg <= 3 ? neon.green + "cc" : d.avg <= 6 ? neon.yellow + "cc" : neon.magenta + "cc";
  const monthLabels = Array.from({ length: weeks }, (_, w) => {
    const d = days[w * 7];
    return (w === 0 || d.date.getDate() <= 7) ? d.date.toLocaleDateString("en-US", { month: "short" }) : "";
  });
  return (
    <div style={{ background: "#080818", border: "1px solid #1a1a3a", borderRadius: 16, padding: 24, ...glowStyle(neon.green, 4) }}>
      <h2 style={{ margin: "0 0 4px", color: neon.green, ...textGlow(neon.green), fontSize: 18 }}>Pain Heatmap</h2>
      <p style={{ color: "#555", fontSize: 12, margin: "0 0 16px" }}>Green = low · Yellow = moderate · Pink = high · Grey = no log</p>
      <div style={{ overflowX: "auto" }}>
        <div style={{ display: "flex", gap: 3, minWidth: "fit-content" }}>
          {Array.from({ length: weeks }, (_, w) => (
            <div key={w} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <div style={{ color: "#444", fontSize: 9, height: 14, textAlign: "center" }}>{monthLabels[w]}</div>
              {days.slice(w * 7, w * 7 + 7).map((d, di) => (
                <div key={di} title={`${d.date.toDateString()}${d.count ? ` — ${d.count} log(s), avg pain: ${d.avg?.toFixed(1)}` : " — no logs"}`}
                  style={{ width: 14, height: 14, borderRadius: 3, background: cellColor(d), cursor: d.count ? "pointer" : "default", transition: "transform 0.1s" }}
                  onMouseOver={e => e.target.style.transform = "scale(1.3)"} onMouseOut={e => e.target.style.transform = "scale(1)"} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ExportButton({ logs }) {
  const exportCSV = () => {
    const headers = ["Date", "Time", "Type", "Amount", "Unit", "Pain", "Notes"];
    const rows = [...logs].sort((a, b) => new Date(a.datetime) - new Date(b.datetime)).map(l => {
      const d = new Date(l.datetime);
      return [d.toLocaleDateString(), d.toLocaleTimeString(), l.type, l.amount, UNITS[l.type], l.pain, l.notes || ""].join(",");
    });
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "medlog-export.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  return <button onClick={exportCSV} style={{ padding: "10px 20px", borderRadius: 10, border: "1px solid " + neon.green, background: "transparent", color: neon.green, fontSize: 14, fontWeight: 700, cursor: "pointer", ...textGlow(neon.green) }}>↓ Export CSV</button>;
}

// ── Week Calendar ─────────────────────────────────────────────────────────────
function WeekCalendar({ logs }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [selected, setSelected] = useState(null);
  const getWeekDays = (offset) => {
    const now = new Date(), day = now.getDay(), monday = new Date(now);
    monday.setDate(now.getDate() - ((day + 6) % 7) + offset * 7);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d; });
  };
  const days = getWeekDays(weekOffset);
  const logsForDay = d => logs.filter(l => new Date(l.datetime).toDateString() === d.toDateString());
  const avgPain = dl => dl.length ? (dl.reduce((s, l) => s + l.pain, 0) / dl.length).toFixed(1) : null;
  const selLogs = selected ? logsForDay(selected) : [];
  return (
    <div style={{ background: "#080818", border: "1px solid #1a1a3a", borderRadius: 16, padding: 24, ...glowStyle(neon.purple, 4) }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: neon.purple, ...textGlow(neon.purple), fontSize: 18 }}>Week View</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={() => setWeekOffset(w => w - 1)} style={{ background: "none", border: "1px solid #333", borderRadius: 6, color: "#aaa", padding: "4px 10px", cursor: "pointer" }}>‹</button>
          <span style={{ color: "#888", fontSize: 13 }}>{days[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – {days[6].toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
          <button onClick={() => setWeekOffset(w => w + 1)} style={{ background: "none", border: "1px solid #333", borderRadius: 6, color: "#aaa", padding: "4px 10px", cursor: "pointer" }}>›</button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
        {days.map((d, i) => {
          const dl = logsForDay(d), ap = avgPain(dl);
          const isToday = d.toDateString() === new Date().toDateString();
          const isSel = selected && selected.toDateString() === d.toDateString();
          const painColor = !ap ? "#333" : +ap <= 3 ? neon.green : +ap <= 6 ? neon.yellow : neon.magenta;
          return (
            <div key={i} onClick={() => setSelected(isSel ? null : d)} style={{ borderRadius: 10, padding: "10px 6px", textAlign: "center", cursor: "pointer", border: `1px solid ${isSel ? neon.purple : isToday ? neon.cyan + "88" : "#1a1a2e"}`, background: isSel ? neon.purple + "22" : isToday ? "#0a0a2a" : "#0d0d1e", transition: "all 0.2s" }}>
              <div style={{ color: "#666", fontSize: 11, marginBottom: 4 }}>{["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i]}</div>
              <div style={{ color: isToday ? neon.cyan : "#ccc", fontSize: 15, fontWeight: 700 }}>{d.getDate()}</div>
              {dl.length > 0 ? <><div style={{ color: neon.cyan, fontSize: 11, marginTop: 4 }}>{dl.length} log{dl.length > 1 ? "s" : ""}</div><div style={{ color: painColor, fontSize: 11 }}>Pain: {ap}</div></> : <div style={{ color: "#333", fontSize: 20, marginTop: 4 }}>·</div>}
            </div>
          );
        })}
      </div>
      {selected && selLogs.length > 0 && (
        <div style={{ marginTop: 16, borderTop: "1px solid #1a1a3a", paddingTop: 16 }}>
          <h3 style={{ color: neon.purple, margin: "0 0 12px", fontSize: 14 }}>{selected.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</h3>
          {selLogs.map(l => (
            <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, padding: "8px 12px", background: "#0d0d1e", borderRadius: 8 }}>
              <TypeBadge type={l.type} /><span style={{ color: "#ccc", fontSize: 13 }}>{l.amount} {UNITS[l.type]}</span>
              <span style={{ color: "#555", fontSize: 12 }}>{new Date(l.datetime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              <span style={{ marginLeft: "auto" }}><PainBadge val={l.pain} /></span>
            </div>
          ))}
        </div>
      )}
      {selected && selLogs.length === 0 && <div style={{ marginTop: 16, borderTop: "1px solid #1a1a3a", paddingTop: 16, color: "#555", textAlign: "center", fontSize: 14 }}>No entries for this day.</div>}
    </div>
  );
}

// ── Charts ────────────────────────────────────────────────────────────────────
function Charts({ logs }) {
  const last14 = () => Array.from({ length: 14 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (13 - i));
    const dl = logs.filter(l => new Date(l.datetime).toDateString() === d.toDateString());
    return { label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }), pain: dl.length ? +(dl.reduce((s, l) => s + l.pain, 0) / dl.length).toFixed(1) : null, logs: dl.length };
  });
  const typeData = () => { const c = {}; TYPES.forEach(t => c[t] = 0); logs.forEach(l => { c[l.type] = (c[l.type] || 0) + 1; }); return TYPES.map(t => ({ name: t, count: c[t] })); };
  const data = last14(), tData = typeData();
  const tt = { background: "#0a0a1a", border: "1px solid #333", borderRadius: 8, color: "#fff" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ background: "#080818", border: "1px solid #1a1a3a", borderRadius: 16, padding: 20, ...glowStyle(neon.magenta, 4) }}>
          <h3 style={{ margin: "0 0 16px", color: neon.magenta, fontSize: 15, ...textGlow(neon.magenta) }}>Pain Trend (14 days)</h3>
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={data}>
              <XAxis dataKey="label" tick={{ fill: "#555", fontSize: 10 }} tickLine={false} axisLine={false} interval={2} />
              <YAxis domain={[0, 10]} tick={{ fill: "#555", fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tt} />
              <Area type="monotone" dataKey="pain" fill={neon.magenta + "11"} stroke={neon.magenta} strokeWidth={2} dot={{ fill: neon.magenta, r: 3 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background: "#080818", border: "1px solid #1a1a3a", borderRadius: 16, padding: 20, ...glowStyle(neon.cyan, 4) }}>
          <h3 style={{ margin: "0 0 16px", color: neon.cyan, fontSize: 15, ...textGlow(neon.cyan) }}>Usage by Type</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={tData}>
              <XAxis dataKey="name" tick={{ fill: "#555", fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "#555", fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tt} />
              <Bar dataKey="count" fill={neon.cyan} radius={[4, 4, 0, 0]} style={{ filter: `drop-shadow(0 0 6px ${neon.cyan})` }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div style={{ background: "#080818", border: "1px solid #1a1a3a", borderRadius: 16, padding: 20, ...glowStyle(neon.green, 4) }}>
        <h3 style={{ margin: "0 0 16px", color: neon.green, fontSize: 15, ...textGlow(neon.green) }}>Usage Frequency (14 days)</h3>
        <ResponsiveContainer width="100%" height={140}>
          <BarChart data={data}>
            <XAxis dataKey="label" tick={{ fill: "#555", fontSize: 10 }} tickLine={false} axisLine={false} interval={2} />
            <YAxis allowDecimals={false} tick={{ fill: "#555", fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tt} />
            <Bar dataKey="logs" fill={neon.green} radius={[4, 4, 0, 0]} style={{ filter: `drop-shadow(0 0 6px ${neon.green})` }} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Heatmap logs={logs} />
      <Insights logs={logs} />
    </div>
  );
}

// ── History ───────────────────────────────────────────────────────────────────
function LogList({ logs, onDelete, onEdit }) {
  const [confirmId, setConfirmId] = useState(null);
  const sorted = [...logs].sort((a, b) => new Date(b.datetime) - new Date(a.datetime)).slice(0, 30);
  return (
    <div style={{ background: "#080818", border: "1px solid #1a1a3a", borderRadius: 16, padding: 24, ...glowStyle(neon.cyan, 4) }}>
      {confirmId && <ConfirmModal message="Delete this entry?" onConfirm={() => { onDelete(confirmId); setConfirmId(null); }} onCancel={() => setConfirmId(null)} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: neon.cyan, ...textGlow(neon.cyan), fontSize: 18 }}>History</h2>
        <ExportButton logs={logs} />
      </div>
      {sorted.length === 0 && <p style={{ color: "#444", textAlign: "center", padding: "20px 0" }}>No entries yet.</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {sorted.map(l => (
          <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "#0d0d1e", borderRadius: 10, border: "1px solid #1a1a2e" }}>
            <TypeBadge type={l.type} />
            <span style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>{l.amount} {UNITS[l.type]}</span>
            <span style={{ color: "#555", fontSize: 12 }}>{new Date(l.datetime).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            {l.notes && <span style={{ color: "#666", fontSize: 12, fontStyle: "italic", flex: 1 }}>"{l.notes}"</span>}
            <span style={{ marginLeft: "auto" }}><PainBadge val={l.pain} /></span>
            <button onClick={() => onEdit(l)} style={{ background: "none", border: "none", color: neon.cyan, cursor: "pointer", fontSize: 14, padding: "0 4px" }}>✏️</button>
            <button onClick={() => setConfirmId(l.id)} style={{ background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 16, padding: "0 4px" }}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
const TABS = ["Home", "Log", "Week", "Charts", "History", "Storage", "Profile"];

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({ defaultPain: 5, defaultType: "Smoked", showStreak: true });
  const [tab, setTab] = useState("Home");
  const [editEntry, setEditEntry] = useState(null);
  const [showQuickLog, setShowQuickLog] = useState(false);

  // Auth listener
  useEffect(() => {
    return onAuthStateChanged(auth, u => { setUser(u); setAuthLoading(false); });
  }, []);

  // Firestore listeners
  useEffect(() => {
    if (!user) return;
    const unsubLogs = onSnapshot(query(logsCol(user.uid)), snap => setLogs(snap.docs.map(d => d.data())));
    const unsubProds = onSnapshot(query(productsCol(user.uid)), snap => setProducts(snap.docs.map(d => d.data())));
    const unsubSettings = onSnapshot(settingsDoc(user.uid), snap => { if (snap.exists()) setSettings(snap.data()); });
    return () => { unsubLogs(); unsubProds(); unsubSettings(); };
  }, [user]);

  const addLog = async (entry) => {
    if (entry.productId) {
      const p = products.find(p => String(p.id) === String(entry.productId));
      if (p) await fsSet(productsCol(user.uid), p.id, { ...p, currentQty: Math.max(0, p.currentQty - +entry.amount) });
    }
    await fsSet(logsCol(user.uid), entry.id, entry);
    setShowQuickLog(false);
  };
  const updateLog = async (entry) => { await fsSet(logsCol(user.uid), entry.id, entry); setEditEntry(null); setTab("History"); };
  const deleteLog = async (id) => { await fsDel(logsCol(user.uid), id); };
  const addProduct = async (p) => { await fsSet(productsCol(user.uid), p.id, p); };
  const restockProduct = async (id, amt) => {
    const p = products.find(p => p.id === id);
    if (p) await fsSet(productsCol(user.uid), id, { ...p, currentQty: p.currentQty + amt, startQty: Math.max(p.startQty, p.currentQty + amt) });
  };
  const deleteProduct = async (id) => { await fsDel(productsCol(user.uid), id); };
  const saveSettings = async (s) => { await setDoc(settingsDoc(user.uid), s); };

  if (authLoading) return <div style={{ background: "#05050f", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: neon.cyan, fontSize: 18, ...textGlow(neon.cyan) }}>◈ Loading...</div>;
  if (!user) return <LoginScreen />;

  return (
    <div style={{ minHeight: "100vh", background: "#05050f", fontFamily: "'Segoe UI', system-ui, sans-serif", position: "relative" }}>
      <Analytics />
      <SpeedInsights />
      <Particles />
      {showQuickLog && (
        <div style={{ position: "fixed", inset: 0, background: "#000000bb", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ width: "100%", maxWidth: 500 }}>
            <LogForm onSave={addLog} onCancel={() => setShowQuickLog(false)} products={products} settings={settings} />
          </div>
        </div>
      )}
      <div style={{ position: "relative", zIndex: 1, maxWidth: 900, margin: "0 auto", padding: "24px 16px 80px" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 900, letterSpacing: 3, color: neon.cyan, ...textGlow(neon.cyan) }}>◈ MEDLOG</h1>
          <p style={{ color: "#444", fontSize: 13, margin: "6px 0 0", letterSpacing: 2 }}>MEDICINAL CANNABIS TRACKER</p>
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 24, background: "#080818", borderRadius: 12, padding: 6, border: "1px solid #1a1a2e", flexWrap: "wrap" }}>
          {TABS.map(t => (
            <button key={t} onClick={() => { setTab(t); setEditEntry(null); }} style={{ flex: "1 1 auto", padding: "10px 6px", borderRadius: 8, border: "none", cursor: "pointer", background: tab === t ? neon.cyan + "22" : "transparent", color: tab === t ? neon.cyan : "#555", fontWeight: tab === t ? 700 : 400, fontSize: 13, transition: "all 0.2s", ...(tab === t ? textGlow(neon.cyan) : {}) }}>{t}</button>
          ))}
        </div>

        {tab === "Home" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <DailySummary logs={logs} products={products} settings={settings} />
            <div style={{ display: "flex", gap: 12 }}>
              {[{ label: "Total Logs", val: logs.length, color: neon.cyan }, { label: "This Week", val: logs.filter(l => (new Date() - new Date(l.datetime)) / 86400000 < 7).length, color: neon.purple }, { label: "Avg Pain", val: logs.length ? (logs.reduce((s, l) => s + l.pain, 0) / logs.length).toFixed(1) : "—", color: neon.magenta }].map(s => (
                <div key={s.label} style={{ flex: 1, background: "#080818", border: `1px solid ${s.color}33`, borderRadius: 12, padding: "12px 16px", textAlign: "center" }}>
                  <div style={{ color: s.color, fontSize: 22, fontWeight: 900, ...textGlow(s.color) }}>{s.val}</div>
                  <div style={{ color: "#555", fontSize: 12, marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <Insights logs={logs} />
          </div>
        )}
        {tab === "Log" && (editEntry
          ? <LogForm initialValues={editEntry} onSave={updateLog} onCancel={() => { setEditEntry(null); setTab("History"); }} products={products} settings={settings} />
          : <LogForm onSave={addLog} products={products} settings={settings} />
        )}
        {tab === "Week" && <WeekCalendar logs={logs} />}
        {tab === "Charts" && <Charts logs={logs} />}
        {tab === "History" && <LogList logs={logs} onDelete={deleteLog} onEdit={e => { setEditEntry(e); setTab("Log"); }} />}
        {tab === "Storage" && <StorageTab products={products} onAdd={addProduct} onRestock={restockProduct} onDelete={deleteProduct} />}
        {tab === "Profile" && <ProfileTab user={user} settings={settings} onSaveSettings={saveSettings} onSignOut={() => signOut(auth)} />}
      </div>

      <button onClick={() => setShowQuickLog(true)} style={{ position: "fixed", bottom: 24, right: 24, width: 56, height: 56, borderRadius: "50%", border: "2px solid " + neon.cyan, background: "#05050f", color: neon.cyan, fontSize: 28, fontWeight: 300, cursor: "pointer", zIndex: 40, lineHeight: 1, ...glowStyle(neon.cyan, 8), ...textGlow(neon.cyan), transition: "transform 0.2s" }}
        onMouseOver={e => e.currentTarget.style.transform = "scale(1.1)"} onMouseOut={e => e.currentTarget.style.transform = "scale(1)"}>+</button>
    </div>
  );
}
